import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit, signal } from '@angular/core';
import { ProductCategoryEnum } from '@mwe/constants';
import {
  getTarifBinding,
  getTariffOptionLabel,
  getTariffOptionTranslationParams,
  getTarifIndication,
  isArrayWithMinOneItem,
  isProductCategory,
  isProductCategoryEmobility,
  isProductCategoryFernwaerme,
  isProductCategoryInternet,
  isProductCategoryStromOrGas,
  mergeSapT3TariffOptions,
} from '@mwe/utils';
import {
  IProduktSelektion,
  PreisIndikation,
  Product,
  ProduktAttribute,
  TarifOption,
  TextNodeType,
  translateTariffClasses,
} from '@mwe/models';
import { EnvironmentService, PriceSheetService, RelocationService, TariffSelectionStateService } from '@mwe/services';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import { HeadlineService } from '../../headline/headline.service';
import { AsyncPipe, NgClass } from '@angular/common';
import { ProductIconComponent } from '../../product-icon/product-icon.component';
import { ProductCardTariffListComponent } from '../product-card-tariff-list/product-card-tariff-list.component';
import { ButtonLinkComponent } from '../../buttons/button-link/button-link.component';
import { HeadlineComponent } from '../../headline/headline.component';
import { ProductCardTariffListFernwaermeComponent } from '../product-card-tariff-list/product-card-tariff-list-fernwaerme.component';
import { InfoTextIconComponent } from '../../info-text/info-text-icon.component';
import { InfoboxComponent } from '../../infobox/infobox.component';
import { CorrelatedCompanyReferencePipe } from '@mwe/ui/pipes';

@Component({
  selector: 'mwe-wizard-product-card',
  templateUrl: './wizard-product-card.component.html',
  providers: [HeadlineService],
  styles: [],
  imports: [
    NgClass,
    ProductIconComponent,
    ProductCardTariffListComponent,
    TranslateDirective,
    ButtonLinkComponent,
    HeadlineComponent,
    ProductCardTariffListFernwaermeComponent,
    AsyncPipe,
    InfoTextIconComponent,
    InfoboxComponent,
    CorrelatedCompanyReferencePipe,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WizardProductCardComponent implements OnInit {
  private relocationService = inject(RelocationService);
  private translateService = inject(TranslateService);
  priceSheetService = inject(PriceSheetService);
  private tariffSelectionStateService = inject(TariffSelectionStateService);
  private environmentService = inject(EnvironmentService);
  headlineService = inject(HeadlineService);

  product = input<Product>();
  showTariff = input<boolean>(true);
  showPrices = input<boolean>(true);
  showTariffDetailsLink = input<boolean>(false);
  showTariffDetails = input<boolean>(false);
  isAvailable = input<boolean>(true);
  tariffClasses = input<string[]>();
  tarifKey = input<string>();
  productSelection = input<IProduktSelektion>();
  grundpreisLabelOverwrite = input<string>();
  voucherInfo = input<string[]>();
  accountNrLabel = input<string>();
  translateParams = input<any>();
  showWaermeTariffClassesAsideOfIcon = input<boolean>(false);
  headingNodeType = input<TextNodeType>('h2');

  grundpreisLabel = signal<string>(undefined);
  tariffClasses$ = signal<Promise<string[]>>(undefined);

  tariffIndication = signal<PreisIndikation>(undefined);
  selectedTariffOptions = signal<TarifOption[]>([]);

  indications = signal<PreisIndikation[]>(undefined);
  tariffBinding = signal<ProduktAttribute>(undefined);

  basePriceKey = signal<string>('tariff.details.basePrice');
  installationPriceKey = signal<string>('tariff.details.installationPrice');
  isCategoryInternet = signal<boolean>(false);
  isCategoryIpTv = signal<boolean>(false);
  isCategoryFernwaerme = signal<boolean>(false);
  discountInfoPopupText = signal<string>(undefined);

  derivedShowTariffDetails = computed(() => this.showTariffDetails() && !this.isCategoryFernwaerme() && this.isAvailable());

  async ngOnInit(): Promise<void> {
    this.headlineService.setReferenceHeadline(this.headingNodeType());
    this.discountInfoPopupText.set(`tariff.isDiscounted.infoIcon.text.${this.environmentService.getPortalCustomerType()}`);
    this.isCategoryFernwaerme.set(isProductCategoryFernwaerme(this.product().category));
    this.grundpreisLabel.set(this.grundpreisLabelOverwrite() ?? 'tariff.details.grundpreis');
    this.tariffClasses$.set(this.getTariffClasses());
    if (this.derivedShowTariffDetails()) {
      this.indications.set(this.tariffSelectionStateService.indications);
    }
    this.tariffIndication.set(getTarifIndication(this.indications(), this.tarifKey()));
    if (isProductCategoryInternet(this.product().category)) {
      this.basePriceKey.set('tariff.details.basePriceInternet');
      this.installationPriceKey.set('tariff.details.installationPrice' + this.product().category);
      this.isCategoryInternet.set(isProductCategory(this.product().category, ProductCategoryEnum.INTERNET));
      this.isCategoryIpTv.set(isProductCategory(this.product().category, ProductCategoryEnum.IPTV));
    }
    this.tariffBinding.set(getTarifBinding(this.tariffIndication()));
    this.setTariffOptions();
  }

  getLabelFrom(option: TarifOption): string {
    const labelKey = getTariffOptionLabel(option, true);
    const params = getTariffOptionTranslationParams(option);
    return this.translateService.instant(labelKey, params);
  }

  getTariffClasses(): Promise<string[]> {
    if (this.tariffClasses()) {
      return this.getTranslateTariffClasses();
    } else {
      return Promise.resolve([]);
    }
  }

  getTranslateTariffClasses(): Promise<string[]> {
    return translateTariffClasses(
      this.product().category,
      this.tariffClasses().filter(c => c),
      this.translateService,
    );
  }

  getTariffShortcut(): string {
    return isProductCategoryEmobility(this.product().category) ? 'tarifblatt-tanke/' : this.readTariffsFromConfig();
  }

  getTariffDetailsLink() {
    const category = this.product().category;
    let pdfUrl = this.environmentService.getDocumentsUrl();
    if (isProductCategoryInternet(category)) {
      if (isProductCategory(category, ProductCategoryEnum.INTERNET)) {
        pdfUrl = pdfUrl.concat('/entgeltbestimmungen-und-leistungsbeschreibungen-von-superschnell-internet/');
      } else if (isProductCategory(category, ProductCategoryEnum.VOIP)) {
        pdfUrl = pdfUrl.concat('/entgeltbestimmungen-und-leistungsbeschreibungen-von-superschnell-festnetz-telefonie/');
      } else if (isProductCategory(category, ProductCategoryEnum.IPTV)) {
        pdfUrl = pdfUrl.concat('/entgeltbestimmungen-und-leistungsbeschreibungen-von-superschnell-tv/');
      }
    } else {
      pdfUrl += this.getTariffShortcut();
    }
    return pdfUrl;
  }

  readTariffsFromConfig(): string {
    const customerType = this.environmentService.getPortalCustomerType();
    const categoryConfig = this.relocationService.getConfigTariffsForCategory(this.product().category.toLowerCase())?.[customerType];
    return this.tarifKey() ? this.tarifKey() : categoryConfig ? categoryConfig.shortcut : '';
  }

  private setTariffOptions(): void {
    this.selectedTariffOptions.set(mergeSapT3TariffOptions(this.getTariffOptions(), this.tariffIndication()?.tarif?.tarifOptionen));
  }

  private getTariffOptions(): TarifOption[] {
    let tariffOptions: TarifOption[] = [];

    if (isArrayWithMinOneItem(this.productSelection()?.anlagen)) {
      const correspondingAnlage = this.productSelection().anlagen.find(anlage => anlage.anlageId === this.product().anlageId);
      if (correspondingAnlage) {
        tariffOptions = correspondingAnlage.tarif.tarifOptionen;
      }
    } else if (
      this.productSelection() &&
      (isProductCategoryStromOrGas(this.product().category) || isProductCategoryInternet(this.product().category))
    ) {
      tariffOptions = this.productSelection()[this.product().category.toLowerCase()].tarif.tarifOptionen;
    }

    return tariffOptions;
  }
}
